import { useEffect, useState } from "react";
import { ExternalLink, Flame, Gauge, Timer } from "lucide-react";
import { api } from "@/lib/api";
import { CopyButton } from "@/components/iv/scorecard/Bits";
import { ReservationNotice } from "@/components/iv/concept/ReservationNotice";

const KitTile = ({ n, title, text, testId }) => (
  <div className="iv-glass flex h-full min-w-0 flex-col gap-2 rounded-xl p-4" data-testid={testId}>
    <div className="flex items-center justify-between gap-2">
      <p className="text-xs font-bold text-white"><span className="font-mono text-cyan-300">{n}.</span> {title}</p>
      <CopyButton text={text} testId={`${testId}-copy`} />
    </div>
    <p className="whitespace-pre-line text-xs leading-relaxed text-slate-300">{text}</p>
  </div>
);

export const DiscoveryKit = ({ script }) => (
  <section aria-labelledby="kit-title" data-testid="discovery-kit">
    <h2 id="kit-title" className="mb-3 text-lg font-bold text-white">Customer Discovery Script Kit</h2>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <KitTile n="01" title="Exact Target Sample & Channels" text={`${script.target_segment}\nChannels: ${(script.channels || []).join(", ")}`} testId="kit-tile-1" />
      <KitTile n="02" title="7-Day Go/Kill Pass Threshold" text={script.pass_threshold} testId="kit-tile-2" />
      <KitTile n="03" title="B2B Decision-Maker / Partner DM Script" text={script.b2b_script} testId="kit-tile-3" />
      <KitTile n="04" title="End-User Interview Opener" text={script.b2c_script} testId="kit-tile-4" />
    </div>
  </section>
);

const useReservation = (report, isOwner) => {
  const [own, setOwn] = useState(null);
  const id = report.evaluation.id;
  useEffect(() => {
    setOwn(null);
    if (!isOwner) return;
    api.get(`/evaluations/${id}/preview`).then(({ data }) => data.reservation && setOwn({ id, slug: data.preview?.subdomain_slug, reservation: data.reservation })).catch(() => null);
  }, [id, isOwner]);
  if (isOwner) return own?.id === id ? own : null;
  const p = report.website_previews?.find((w) => w.is_safe_pivot_preview) || report.website_previews?.[0];
  return { slug: p?.subdomain_slug, reservation: { never_expires: true, reason: "reference" } };
};

export const ExecutionWindow = ({ report, onStage, isOwner = false }) => {
  const [stat, setStat] = useState(null);
  const res = useReservation(report, isOwner);
  useEffect(() => { api.get("/settings/public").then((r) => setStat(r.data.execution_window_stat)).catch(() => setStat(null)); }, []);
  const ew = report.evaluation.execution_window || {};
  const tile = "iv-glass flex h-full min-w-0 flex-col gap-2 rounded-xl p-4";
  return (
    <section aria-labelledby="window-title" data-testid="execution-window">
      <h2 id="window-title" className="mb-3 text-lg font-bold text-white">Founder Behavioral Momentum & Execution Window</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className={tile} data-testid="window-tile-stat">
          <Timer className="h-4 w-4 text-cyan-300" aria-hidden="true" />
          <p className="font-mono text-3xl font-bold text-white" data-testid="window-stat-value">{stat ? `${stat.value}${stat.unit}` : "—"}</p>
          <p className="text-xs font-bold text-slate-200">{stat?.label}</p>
          <p className="text-xs text-slate-300" data-testid="window-stat-benchmark">{stat?.description}</p>
          <p className="mt-auto text-[10px] text-slate-500" data-testid="window-stat-source">{stat?.source_label}</p>
        </div>
        <div className={tile} data-testid="window-tile-zone">
          <Gauge className="h-4 w-4 text-emerald-300" aria-hidden="true" />
          <p className="text-xs font-bold text-slate-200">Score Zone Interpretation</p>
          <p className="text-xs leading-relaxed text-slate-300">{ew.score_zone_interpretation || report.evaluation.verdict_label}</p>
        </div>
        <div className={tile} data-testid="window-tile-stall">
          <Flame className="h-4 w-4 text-rose-300" aria-hidden="true" />
          <p className="text-xs font-bold text-slate-200">#1 Sector Stall Reason</p>
          <p className="text-xs leading-relaxed text-slate-300">{ew.sector_stall_reason || "—"}</p>
        </div>
        <div className={tile} data-testid="window-tile-preview">
          <ExternalLink className="h-4 w-4 text-violet-300" aria-hidden="true" />
          <p className="text-xs font-bold text-slate-200">Act inside the window</p>
          <p className="text-xs text-slate-400">Open your native Test Website Preview now — without leaving IdeaValidator360.</p>
          <button type="button" onClick={() => onStage(4)} className="iv-cta mt-auto rounded-full px-3 py-2 text-xs font-bold text-white" data-testid="window-open-preview-button">
            Open Test Website Preview
          </button>
        </div>
      </div>
      {res && <div className="mt-3"><ReservationNotice slug={res.slug} reservation={res.reservation} /></div>}
    </section>
  );
};
